/**
 * First-login guided-tours welcome prompt (EPIC #1321, part 3/9; D4-ONB-006).
 *
 * Shown EXACTLY ONCE, on the user's own landing page, when ``/me`` reports
 * ``welcome_prompt_eligible`` (set when their invite link was issued). An opt-in,
 * branded greeting offering the matching persona welcome tour:
 *   - "Take the tour"        -> clears eligibility + starts the persona tour.
 *   - "I'll explore on my own" -> clears eligibility, no tour.
 * Either way the prompt is answered server-side so it never re-appears on
 * another device or session (the X / Esc / backdrop are treated as "explore on
 * my own"). A line notes that tours live under Training so they can be replayed.
 *
 * Mounted once globally (inside TourProvider) so it covers admin, agent and the
 * back-office landings without per-layout wiring. It self-suppresses unless the
 * user is actually ON their own landing path (``landingPathForUser``) -- so an
 * elevated user browsing the agent CRM is not interrupted on a non-landing page.
 *
 * Copy is agency-neutral via BrandingContext (D4-ONB-011 / D4-WLB) and carries
 * no emojis. Uses the shared Modal primitive (bottom-sheet on mobile, focus
 * trap, Escape/backdrop close) -- the house modal pattern.
 */
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Compass, GraduationCap } from "lucide-react";
import Modal from "../ui/Modal";
import { useAuth } from "../../contexts/auth";
import { useTour } from "../../contexts/tour";
import { useIsViewingAs } from "../../contexts/viewAs";
import { useBranding } from "../../hooks/useBranding";
import { landingPathForUser } from "../auth/ProtectedRoute";
import { welcomeTourIdForUser } from "../../lib/tours/registry";
import { answerWelcomePrompt } from "../../api/tours";

export function WelcomePrompt() {
  const { user, refreshUser } = useAuth();
  const { startTour } = useTour();
  const branding = useBranding();
  const location = useLocation();
  // Answering is a refused write while viewing as an agent (D2-SEC-011, #3962).
  const viewingAs = useIsViewingAs();

  // Local "open" latch: once the user answers we hide immediately (the server
  // round-trip + /me refresh happen in the background). Keyed off the user id so
  // a different user in the same tab re-evaluates cleanly.
  const [dismissed, setDismissed] = useState(false);
  const answeringRef = useRef(false);
  const lastUserId = useRef<number | null>(null);

  useEffect(() => {
    if (user?.id !== lastUserId.current) {
      lastUserId.current = user?.id ?? null;
      setDismissed(false);
      answeringRef.current = false;
    }
  }, [user?.id]);

  if (!user || viewingAs || dismissed || !user.welcome_prompt_eligible)
    return null;

  // Only greet the user on their OWN landing surface, never mid-browse.
  const landing = landingPathForUser(user.role, user.permission_preset);
  if (location.pathname !== landing) return null;

  const agency = branding.agency_name;
  const tourId = welcomeTourIdForUser(user.role, user.permission_preset);

  /** Clear eligibility server-side, then refresh the cached user. */
  async function answer() {
    if (answeringRef.current) return;
    answeringRef.current = true;
    setDismissed(true);
    try {
      await answerWelcomePrompt();
    } catch {
      // Non-fatal: the prompt is already hidden locally. A failed clear simply
      // means it may re-appear next session, which is acceptable.
    }
    try {
      await refreshUser();
    } catch {
      /* ignore */
    }
  }

  function takeTour() {
    void answer();
    startTour(tourId);
  }

  function exploreOnMyOwn() {
    void answer();
  }

  return (
    <Modal
      open
      onClose={exploreOnMyOwn}
      size="md"
      title={`Welcome to ${agency}`}
      titleIcon={<Compass className="h-5 w-5 text-brand-accent" />}
      closeLabel="Close and explore on my own"
      feedbackTrigger={false}
      footer={
        <>
          <button
            type="button"
            onClick={takeTour}
            className="min-h-[44px] px-4 py-2.5 rounded-lg bg-brand-accent text-white font-medium hover:opacity-90 transition-opacity"
          >
            Take the tour
          </button>
          <button
            type="button"
            onClick={exploreOnMyOwn}
            className="min-h-[44px] px-4 py-2.5 rounded-lg border border-[var(--color-border)] text-[var(--color-text-secondary)] font-medium hover:bg-[var(--color-bg-page)] transition-colors"
          >
            I'll explore on my own
          </button>
        </>
      }
    >
      <div className="px-5 sm:px-6 py-5 space-y-3 text-[var(--color-text-secondary)]">
        <p>
          A quick guided tour shows you around your {agency} workspace. It takes
          about two minutes, and you can stop at any time.
        </p>
        <p className="flex items-start gap-2 text-sm text-[var(--color-text-muted)]">
          <GraduationCap className="h-4 w-4 mt-0.5 shrink-0" />
          <span>
            You can take this tour again whenever you like from Training.
          </span>
        </p>
      </div>
    </Modal>
  );
}
